import type { AnalyseBefund, AnalyseSeite, Auftrag } from '@awf/shared';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useAngemeldeterNutzer } from '../auth/auth-context.js';
import { hatFreigabeSeite } from '../auftragstypen.js';
import { LinienSymbol } from '../ui/linien-symbol.js';
import { StatusChip } from '../ui/status-chip.js';
import stile from './analyse-seiten-liste.module.css';
import { istFremd } from './einsicht.js';
import { keywordHinweisKurz } from './keyword-hinweis.js';
import { PDP_BLOECKE } from './pdp-bloecke.js';
import type { AuftragsQuelle } from './runs-source.js';
import { gruppeVon } from './status.js';
import { useAuftrag } from './use-runs.js';
import { type StarteVolleAnalyse, VolleAnalyseKnopf } from './volle-analyse-knopf.js';

const STAND_TEXT: Record<AnalyseSeite['stand'], string> = {
  offen: 'Offen',
  optimiert: 'Wird optimiert',
  verworfen: 'Verworfen',
  ueberholt: 'In neuerer Analyse geprüft',
};

/**
 * Seitenliste einer Ratgeber-Analyse auf der Detailseite (Spec 2026-10-13-ratgeber-analyse-frontend,
 * 6.1). Nur Firestore-Daten; die Details laedt erst die Entscheidungsseite je Seite.
 *
 * Je gepruefter Seite der Grund, das Stand-Datum des Vorfilters und der Weg zur vollen Analyse
 * (Spec 2026-11-03-kein-bedarf, 4.1 und 4.2), unabhaengig davon, ob die Analyse noch wartet.
 */
function GeprueftListe({
  auftrag,
  befund,
  quelle,
  starte,
  fremd,
}: {
  auftrag: Auftrag;
  befund: AnalyseBefund;
  quelle: AuftragsQuelle;
  starte: StarteVolleAnalyse;
  fremd: boolean;
}) {
  return (
    <>
      <ul className={stile.geprueftListe}>
        {befund.geprueft.map((g) => (
          <li key={g.url} className={stile.geprueftEintrag} aria-label={g.h1 || g.url}>
            <div className={stile.geprueftInhalt}>
              <a href={g.url} target="_blank" rel="noreferrer">
                {g.h1 || g.url}
              </a>
              {g.grund && <span className={stile.leise}>{g.grund}</span>}
              {g.standDatum && <span className={stile.leise}>Stand: {g.standDatum}</span>}
            </div>
            <VolleAnalyseKnopf
              key={`${auftrag.runId} ${g.url}`}
              ursprungRunId={auftrag.runId}
              url={g.url}
              kindRunId={g.kindRunId}
              starte={starte}
              quelle={quelle}
              fremd={fremd}
              klein
            />
          </li>
        ))}
      </ul>
      {befund.gepruefteWeitere !== undefined && (
        <p className={stile.leise}>… und {befund.gepruefteWeitere} weitere.</p>
      )}
    </>
  );
}

/**
 * Bekannter Stand eines Kind-Auftrags. `null`: der Server meldet, den Auftrag gibt es nicht.
 * `unbekannt`: Lesefehler oder Ladezeitgrenze. Kein Eintrag: laedt noch.
 */
type KindStand = Auftrag | null | 'unbekannt';

/**
 * Beobachtet einen Kind-Auftrag mit genau der Logik der Detailseite (`useAuftrag`): Vor dem
 * ersten Serverstand zaehlt der Zwischenspeicher nicht, nach `LADE_ZEITGRENZE_MS` ohne Antwort
 * ist der Stand unbekannt. Eine eigene Cache-Regel nur fuer diese Liste haette drei
 * Codex-Befunde derselben Art erzeugt (PR #81).
 *
 * Bewusst Einzel-Listener statt einer Abfrage auf `ausAnalyseRunId`: Die Firestore-Regeln
 * erlauben das Lesen je Dokument schon heute, eine Abfrage braeuchte einen Filter auf
 * `ownerUid` und einen neuen Composite-Index. Eine Analyse hat nur eine Handvoll Kinder.
 */
function KindBeobachter({
  quelle,
  runId,
  melde,
}: {
  quelle: AuftragsQuelle;
  runId: string;
  melde: (runId: string, stand: KindStand | undefined) => void;
}) {
  const zustand = useAuftrag(quelle, runId);
  useEffect(() => {
    melde(
      runId,
      zustand.art === 'laedt'
        ? undefined
        : zustand.art === 'fehler'
          ? 'unbekannt'
          : zustand.auftrag,
    );
  }, [melde, runId, zustand]);
  useEffect(() => () => melde(runId, undefined), [melde, runId]);
  return null;
}

/** Wo eine Seite steht, zusammengefasst aus ihrem Stand in der Analyse und dem Kind-Auftrag. */
type Lage =
  | 'entscheiden'
  | 'nichtEntschieden'
  | 'pruefen'
  | 'inArbeit'
  | 'laedt'
  | 'unbekannt'
  | 'fertig'
  | 'fehler'
  | 'geloescht'
  | 'verworfen'
  | 'ueberholt';

/** Reihenfolge der Zusammenfassung: erst, wo Sie am Zug sind, dann der Rest des Weges. */
const LAGE_TEXT: Record<Lage, string> = {
  entscheiden: 'zu entscheiden',
  nichtEntschieden: 'nicht entschieden',
  pruefen: 'zur Prüfung',
  inArbeit: 'in Arbeit',
  laedt: 'wird geladen',
  unbekannt: 'Stand unbekannt',
  fertig: 'fertig',
  fehler: 'fehlgeschlagen',
  geloescht: 'gelöscht',
  verworfen: 'verworfen',
  ueberholt: 'in neuerer Analyse geprüft',
};

function lageVon(seite: AnalyseSeite, entscheidbar: boolean, kind: KindStand | undefined): Lage {
  if (seite.stand === 'offen') return entscheidbar ? 'entscheiden' : 'nichtEntschieden';
  if (seite.stand === 'verworfen') return 'verworfen';
  if (seite.stand === 'ueberholt') return 'ueberholt';
  // Altbestand ohne Kind-ID: nichts zu beobachten, der Auftrag laeuft laut Analyse.
  if (seite.kindRunId === undefined) return 'inArbeit';
  if (kind === undefined) return 'laedt';
  if (kind === null) return 'geloescht';
  if (kind === 'unbekannt') return 'unbekannt';
  if (hatFreigabeSeite(kind)) return 'pruefen';
  const gruppe = gruppeVon(kind.status);
  if (gruppe === 'fertig') return 'fertig';
  if (gruppe === 'fehler') return 'fehler';
  return 'inArbeit';
}

const AM_ZUG: readonly Lage[] = ['entscheiden', 'pruefen'];

/** Chip-Texte fuer Kind-Staende ohne lesbaren Auftrag; die uebrigen nennt der Stand der Seite. */
const CHIP_TEXT: Partial<Record<Lage, string>> = {
  laedt: 'Stand wird geladen',
  unbekannt: 'Stand unbekannt',
  geloescht: 'Auftrag gelöscht',
};

/** Kein "Zum Optimierungsauftrag": bei "pruefen" fuehrt der Hauptbutton hin, sonst laedt er nicht. */
const OHNE_KIND_LINK: readonly Lage[] = ['pruefen', 'geloescht'];

function Zusammenfassung({ lagen }: { lagen: readonly Lage[] }) {
  const teile = (Object.keys(LAGE_TEXT) as Lage[])
    .map((lage) => ({ lage, anzahl: lagen.filter((l) => l === lage).length }))
    .filter((t) => t.anzahl > 0);
  return (
    <p className={stile.zusammenfassung} role="note" aria-label="Stand der Seiten">
      {teile.map((t, i) => (
        <span key={t.lage}>
          {i > 0 && ' · '}
          {AM_ZUG.includes(t.lage) ? (
            <strong>
              {t.anzahl} {LAGE_TEXT[t.lage]}
            </strong>
          ) : (
            `${t.anzahl} ${LAGE_TEXT[t.lage]}`
          )}
        </span>
      ))}
    </p>
  );
}

export function AnalyseSeitenListe({
  auftrag,
  quelle,
  starte,
}: {
  auftrag: Auftrag;
  quelle: AuftragsQuelle;
  starte: StarteVolleAnalyse;
}) {
  const befund = auftrag.typ === 'analyse' ? auftrag.analyseBefund : undefined;
  // Gesamtansicht der Rolle `admin`: dieselben Wege, aber zum Lesen benannt. Die Zielseiten
  // blenden das Entscheiden selbst aus (entscheidung-spalte, freigabe-route).
  const fremd = istFremd(auftrag, useAngemeldeterNutzer()?.uid);
  const kindIds = [
    ...new Set(
      (befund?.seiten ?? []).flatMap((s) =>
        s.stand === 'optimiert' && s.kindRunId ? [s.kindRunId] : [],
      ),
    ),
  ];
  const [kinder, setKinder] = useState<ReadonlyMap<string, KindStand>>(new Map());
  const melde = useCallback(
    (runId: string, stand: KindStand | undefined) =>
      setKinder((alt) => {
        const neu = new Map(alt);
        if (stand === undefined) neu.delete(runId);
        else neu.set(runId, stand);
        return neu;
      }),
    [],
  );
  if (!befund) return null;
  const id = encodeURIComponent(auftrag.runId);
  // Entscheiden laesst sich nur, solange die Analyse wartet; danach antwortete das Backend mit
  // 409 (Codex-Review PR #73). Offene Seiten einer beendeten Analyse sind "nicht entschieden".
  const entscheidbar = auftrag.status === 'wartet_auf_entscheidung';
  const gepruefteAnzahl = befund.geprueft.length + (befund.gepruefteWeitere ?? 0);
  const zeilen = befund.seiten.map((s) => {
    const kind = s.kindRunId === undefined ? undefined : kinder.get(s.kindRunId);
    return { s, kind, lage: lageVon(s, entscheidbar, kind) };
  });
  // Wo Sie am Zug sind, steht oben (Rueckmeldung 2026-10-14). Sonst bleibt die Reihenfolge
  // der Analyse; sort ist stabil.
  const amZug = (lage: Lage) => (AM_ZUG.includes(lage) ? 0 : 1);
  zeilen.sort((a, b) => amZug(a.lage) - amZug(b.lage));
  return (
    <div className={stile.liste}>
      {kindIds.map((runId) => (
        <KindBeobachter key={runId} quelle={quelle} runId={runId} melde={melde} />
      ))}
      {/* "Kein Bedarf" ist ein gutes Ergebnis und soll so aussehen (Rueckmeldung 2026-10-14):
          gruen, mit Haken, und die geprueften Seiten gleich sichtbar statt eingeklappt. */}
      {befund.seiten.length === 0 && befund.seitenWeitere === undefined && (
        <div className={`eds-alert eds-alert--success ${stile.allesGut}`} role="status">
          <span className={stile.allesGutSymbol}>
            <LinienSymbol name="check" groesse={20} />
          </span>
          <div className={stile.allesGutText}>
            <p className="eds-alert__title">Kein Optimierungsbedarf</p>
            <p>
              {gepruefteAnzahl === 1
                ? 'Die geprüfte Seite ist aktuell. Es gibt nichts zu tun.'
                : gepruefteAnzahl > 1
                  ? `Alle ${gepruefteAnzahl} geprüften Seiten sind aktuell. Es gibt nichts zu tun.`
                  : 'Keine der geprüften Seiten braucht eine Optimierung.'}
            </p>
          </div>
        </div>
      )}
      {zeilen.length > 0 && <Zusammenfassung lagen={zeilen.map((z) => z.lage)} />}
      {zeilen.length > 0 && (
        <ul className={stile.seiten} aria-label="Seiten mit Optimierungsbedarf">
          {zeilen.map(({ s, kind, lage }) => {
            const zurSeite = `/auftraege/${id}/seiten/${encodeURIComponent(s.auditId)}`;
            // Ohne Fokus-Keyword stuenden hier nur Nullen; der Hinweis sagt, warum.
            const bloecke = s.keywordHinweis === 'fehlt' ? undefined : s.bloecke;
            const hinweis = keywordHinweisKurz(s);
            // Nur mit `ueberholtDurch` sind die Daten noch da; Altbestand verlor sie mit der Zeile.
            const neuere = s.stand === 'ueberholt' ? s.ueberholtDurch : undefined;
            return (
              <li key={s.auditId} className={stile.seite} aria-label={s.h1 || s.url}>
                <div className={stile.kopf}>
                  <strong>{s.h1 || s.url}</strong>
                  {s.stand === 'optimiert' && typeof kind === 'object' && kind !== null ? (
                    <StatusChip status={kind.status} typ={kind.typ} />
                  ) : (
                    <span className="eds-chip">
                      {CHIP_TEXT[lage] ??
                        (s.stand === 'offen' && !entscheidbar
                          ? 'Nicht entschieden'
                          : STAND_TEXT[s.stand])}
                    </span>
                  )}
                </div>
                <a className={stile.adresse} href={s.url} target="_blank" rel="noreferrer">
                  {s.url}
                </a>
                {/* Ohne Fokus-Keyword widerspraeche "Optimierung empfohlen" dem Hinweis darunter. */}
                {s.keywordHinweis !== 'fehlt' && (
                  <p className={stile.grund}>{s.grund || 'Optimierung empfohlen'}</p>
                )}
                {hinweis && <p className={stile.hinweis}>{hinweis}</p>}
                {bloecke && (
                  <ul className={stile.chips} aria-label="Empfehlungen je Block">
                    {PDP_BLOECKE.map((b) => (
                      <li key={b.schluessel} className="eds-chip">
                        {b.kurz} {bloecke[b.schluessel]}
                      </li>
                    ))}
                  </ul>
                )}
                {s.stand === 'offen' && entscheidbar && (
                  <div>
                    <Link className="eds-button eds-button--primary eds-button--sm" to={zurSeite}>
                      {fremd ? 'Befund ansehen' : 'Prüfen & entscheiden'}
                    </Link>
                  </div>
                )}
                {lage === 'pruefen' && s.kindRunId && (
                  <div>
                    <Link
                      className="eds-button eds-button--primary eds-button--sm"
                      to={`/auftraege/${encodeURIComponent(s.kindRunId)}/${fremd ? 'ergebnis' : 'freigabe'}`}
                    >
                      {fremd ? 'Zwischenstand ansehen' : 'Entwurf prüfen'}
                    </Link>
                  </div>
                )}
                {s.stand === 'optimiert' && s.kindRunId && !OHNE_KIND_LINK.includes(lage) && (
                  <div>
                    <Link
                      className="eds-button eds-button--secondary eds-button--sm"
                      to={`/auftraege/${encodeURIComponent(s.kindRunId)}`}
                    >
                      Zum Optimierungsauftrag
                    </Link>
                  </div>
                )}
                {(s.stand === 'verworfen' || (s.stand === 'offen' && !entscheidbar) || neuere) && (
                  <Link className={stile.leise} to={zurSeite}>
                    Befund ansehen
                  </Link>
                )}
                {neuere && (
                  <div>
                    <Link
                      className="eds-button eds-button--secondary eds-button--sm"
                      to={`/auftraege/${encodeURIComponent(neuere)}`}
                    >
                      Zur neueren Analyse
                    </Link>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {befund.seitenWeitere !== undefined && (
        <p className="eds-alert eds-alert--warning" role="status">
          {befund.seitenWeitere} weitere Seiten mit Bedarf passen nicht in diese Liste und lassen
          sich hier nicht entscheiden. Starten Sie für sie eine Analyse mit einem engeren
          Verzeichnis.
        </p>
      )}
      {gepruefteAnzahl > 0 &&
        (befund.seiten.length === 0 ? (
          <section aria-labelledby="geprueft-titel" className={stile.geprueftOffen}>
            <h3 id="geprueft-titel" className={stile.geprueftTitel}>
              Geprüfte Seiten ({gepruefteAnzahl})
            </h3>
            <GeprueftListe
              auftrag={auftrag}
              befund={befund}
              quelle={quelle}
              starte={starte}
              fremd={fremd}
            />
          </section>
        ) : (
          <details className={stile.geprueft}>
            <summary>Ohne Optimierungsbedarf ({gepruefteAnzahl})</summary>
            <GeprueftListe
              auftrag={auftrag}
              befund={befund}
              quelle={quelle}
              starte={starte}
              fremd={fremd}
            />
          </details>
        ))}
    </div>
  );
}
